/**
 * ── Billing Access Context ──
 * Bridges platform subscription/billing state with module/feature access enforcement.
 *
 * Resolves the complete access picture for the current entity by combining:
 * 1. Billing state (from useBillingResolution)
 * 2. Commercial catalog mapping (from useBillingToModules)
 * 3. Subscription lifecycle rules (active, trial, past_due, cancelled, suspended)
 *
 * This is the single source of truth for "can this customer access X?"
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useOrg } from "@/contexts/OrgContext";
import { useEntityBillingResolution, type BillingResolution } from "@/hooks/useBillingResolution";
import { useBillingToModules, type BillingModuleResolution } from "@/hooks/useBillingToModules";
import {
  type AccessState,
  type PlanId,
  getMinPlanForModule,
  getMinPlanForCapability,
  getAddOnsForModule,
  getAddOnsForCapability,
  PLAN_LIST,
} from "@/lib/commercial-catalog";

// ── Types ────────────────────────────────────────────────────────────────────

export type BillingStatus =
  | "active"
  | "trial"
  | "past_due"
  | "grace_period"
  | "cancelled_active" // cancelled but still in paid period
  | "suspended"
  | "inactive"
  | "no_subscription";

export interface AccessCheckResult {
  /** Whether the entity can access the module/capability right now */
  allowed: boolean;
  /** The access state classification */
  state: AccessState;
  /** Why access is granted or denied */
  reason: string;
  /** Source of the access grant (if granted) */
  source?: "plan" | "addon" | "trial" | "bonus" | "inherited" | "override";
  /** If denied, which plan would unlock it */
  upgradePlan?: PlanId;
  /** If denied, can an add-on unlock it */
  availableAddon?: string;
}

export interface BillingAccessState {
  /** Current billing lifecycle status */
  billingStatus: BillingStatus;
  /** Whether the entity has usable access (active, trial, or grace period) */
  hasUsableAccess: boolean;
  /** Whether access is degraded (past_due, grace_period) */
  isDegraded: boolean;
  /** Human-readable status message */
  statusMessage: string;
  /** The resolved plan from billing */
  resolvedPlan: PlanId | null;
  /** Days until access expires (for trials, grace periods, cancelled-active) */
  daysUntilExpiry: number | null;
  /** Full billing resolution for advanced use */
  resolution: BillingResolution;
  /** Full billing → module resolution */
  moduleResolution: BillingModuleResolution;
  /** Check if a specific module is accessible */
  checkModuleAccess: (moduleKey: string) => AccessCheckResult;
  /** Check if a specific capability is accessible */
  checkCapabilityAccess: (capabilityKey: string) => AccessCheckResult;
  /** Whether the system is still loading */
  isLoading: boolean;
}

// ── Grace period config ──────────────────────────────────────────────────────

/** Days after past_due before access is suspended */
const GRACE_PERIOD_DAYS = 14;

// ── Context ──────────────────────────────────────────────────────────────────

const BillingAccessContext = createContext<BillingAccessState | null>(null);

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveBillingStatus(resolution: BillingResolution): BillingStatus {
  if (!resolution.hasActiveBilling && resolution.sources.length === 0) {
    return "no_subscription";
  }

  const allStatuses = resolution.sources.map((s) => s.agreement.status);

  // Check for active trial
  if (allStatuses.some((s) => s === "trial" || s === "trialing")) {
    return "trial";
  }

  // Cancelled but still within the paid period
  const cancelledActive = resolution.sources.some(
    (s) =>
      (s.agreement.status === "canceled" || s.agreement.status === "cancelled") &&
      s.agreement.current_period_end &&
      new Date(s.agreement.current_period_end) > new Date(),
  );
  if (cancelledActive) return "cancelled_active";

  // Past due — check if within grace period
  if (resolution.hasPastDue) {
    const pastDueSources = resolution.sources.filter((s) => s.agreement.status === "past_due");
    const oldestPastDue = pastDueSources
      .map((s) => s.agreement.current_period_end)
      .filter(Boolean)
      .sort()[0];

    if (oldestPastDue) {
      const daysSincePastDue = Math.floor((Date.now() - new Date(oldestPastDue).getTime()) / (1000 * 60 * 60 * 24));
      if (daysSincePastDue > GRACE_PERIOD_DAYS) return "suspended";
    }
    return allStatuses.some((s) => s === "active") ? "past_due" : "grace_period";
  }

  // Fully inactive
  if (!resolution.hasActiveBilling) {
    const hasAnySources = resolution.sources.length > 0;
    return hasAnySources ? "inactive" : "no_subscription";
  }

  return "active";
}

function computeDaysUntilExpiry(status: BillingStatus, resolution: BillingResolution): number | null {
  if (status === "active") return null;

  const referenceDate =
    status === "cancelled_active"
      ? resolution.sources
          .filter(
            (s) =>
              (s.agreement.status === "canceled" || s.agreement.status === "cancelled") &&
              s.agreement.current_period_end,
          )
          .map((s) => s.agreement.current_period_end!)
          .sort()
          .pop()
      : status === "trial"
        ? resolution.nextExpiration
        : status === "grace_period" || status === "past_due"
          ? (() => {
              const pastDueEnd = resolution.sources
                .filter((s) => s.agreement.status === "past_due")
                .map((s) => s.agreement.current_period_end)
                .filter(Boolean)
                .sort()[0];
              if (!pastDueEnd) return null;
              const graceEnd = new Date(pastDueEnd);
              graceEnd.setDate(graceEnd.getDate() + GRACE_PERIOD_DAYS);
              return graceEnd.toISOString();
            })()
          : null;

  if (!referenceDate) return null;
  const days = Math.ceil((new Date(referenceDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return Math.max(0, days);
}

function getStatusMessage(status: BillingStatus, daysUntilExpiry: number | null): string {
  switch (status) {
    case "active":
      return "Your subscription is active and in good standing.";
    case "trial":
      return daysUntilExpiry !== null
        ? `Trial active — ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? "s" : ""} remaining.`
        : "Trial active.";
    case "past_due":
      return "Payment is past due. Please update your payment method to avoid service interruption.";
    case "grace_period":
      return daysUntilExpiry !== null
        ? `Payment overdue — access will be suspended in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? "s" : ""}.`
        : "Payment overdue — access will be suspended soon.";
    case "cancelled_active":
      return daysUntilExpiry !== null
        ? `Cancelled — access continues for ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? "s" : ""}.`
        : "Cancelled — access continues until end of billing period.";
    case "suspended":
      return "Access suspended due to unpaid balance. Please update your payment to restore access.";
    case "inactive":
      return "Your subscription is inactive. Reactivate to restore access.";
    case "no_subscription":
      return "No active subscription. Subscribe to unlock MemberOS features.";
  }
}

// ── Provider ─────────────────────────────────────────────────────────────────

interface BillingAccessProviderProps {
  children: ReactNode;
  /** Override entity type for association-level contexts */
  entityType?: "organization" | "association";
}

export function BillingAccessProvider({ children, entityType = "organization" }: BillingAccessProviderProps) {
  const { orgId } = useOrg();

  const resolution = useEntityBillingResolution(orgId, entityType);
  const moduleResolution = useBillingToModules(orgId, entityType);
  const isLoading = resolution.isLoading || moduleResolution.isLoading;

  const value = useMemo((): BillingAccessState => {
    const billingStatus = resolveBillingStatus(resolution);
    const daysUntilExpiry = computeDaysUntilExpiry(billingStatus, resolution);

    const hasUsableAccess = ["active", "trial", "past_due", "grace_period", "cancelled_active"].includes(billingStatus);

    const isDegraded = ["past_due", "grace_period", "cancelled_active"].includes(billingStatus);

    const statusMessage = getStatusMessage(billingStatus, daysUntilExpiry);

    const checkModuleAccess = (moduleKey: string): AccessCheckResult => {
      // Suspended / inactive → always denied
      if (billingStatus === "suspended" || billingStatus === "inactive") {
        const minPlan = getMinPlanForModule(moduleKey);
        return {
          allowed: false,
          state: "locked",
          reason: `Access suspended — ${billingStatus === "suspended" ? "payment required" : "subscription inactive"}`,
          upgradePlan: minPlan?.id as PlanId | undefined,
        };
      }

      // No subscription → denied
      if (billingStatus === "no_subscription") {
        const minPlan = getMinPlanForModule(moduleKey);
        const addons = getAddOnsForModule(moduleKey);
        return {
          allowed: false,
          state: "locked",
          reason: "No active subscription",
          upgradePlan: minPlan?.id as PlanId | undefined,
          availableAddon: addons[0]?.id,
        };
      }

      // Check if billing grants this module
      if (moduleResolution.isModuleGrantedByBilling(moduleKey)) {
        // Determine source
        const isTrial = billingStatus === "trial";
        const source = isTrial ? ("trial" as const) : ("plan" as const);
        const state: AccessState = isTrial ? "trial" : "included";

        return {
          allowed: true,
          state,
          reason: isTrial ? "Included in trial" : `Included in ${moduleResolution.resolvedPlan || "current"} plan`,
          source,
        };
      }

      // Check add-ons
      if (moduleResolution.activeAddOnIds.size > 0) {
        const addons = getAddOnsForModule(moduleKey);
        const activeAddon = addons.find((a) => moduleResolution.activeAddOnIds.has(a.id));
        if (activeAddon) {
          return {
            allowed: true,
            state: "included",
            reason: `Included via ${activeAddon.name} add-on`,
            source: "addon",
          };
        }
      }

      // Check if available as add-on (tease)
      const availableAddons = getAddOnsForModule(moduleKey);
      if (availableAddons.length > 0) {
        return {
          allowed: false,
          state: "addon",
          reason: `Available as add-on: ${availableAddons[0].name}`,
          availableAddon: availableAddons[0].id,
          upgradePlan: moduleResolution.getUpgradePlanForModule(moduleKey) || undefined,
        };
      }

      // Locked — find upgrade path
      const upgradePlan = moduleResolution.getUpgradePlanForModule(moduleKey);
      return {
        allowed: false,
        state: "locked",
        reason: upgradePlan
          ? `Requires ${PLAN_LIST.find((p) => p.id === upgradePlan)?.name || upgradePlan} plan`
          : "Not available on current plan",
        upgradePlan: upgradePlan || undefined,
      };
    };

    const checkCapabilityAccess = (capabilityKey: string): AccessCheckResult => {
      // Suspended / inactive → always denied
      if (billingStatus === "suspended" || billingStatus === "inactive") {
        const minPlan = getMinPlanForCapability(capabilityKey);
        return {
          allowed: false,
          state: "locked",
          reason: `Access suspended`,
          upgradePlan: minPlan?.id as PlanId | undefined,
        };
      }

      if (billingStatus === "no_subscription") {
        const minPlan = getMinPlanForCapability(capabilityKey);
        return {
          allowed: false,
          state: "locked",
          reason: "No active subscription",
          upgradePlan: minPlan?.id as PlanId | undefined,
        };
      }

      if (moduleResolution.isCapabilityGrantedByBilling(capabilityKey)) {
        const isTrial = billingStatus === "trial";
        return {
          allowed: true,
          state: isTrial ? "trial" : "included",
          reason: isTrial ? "Included in trial" : `Included in ${moduleResolution.resolvedPlan || "current"} plan`,
          source: isTrial ? "trial" : "plan",
        };
      }

      // Check add-ons
      const availableAddons = getAddOnsForCapability(capabilityKey);
      if (availableAddons.length > 0) {
        const activeAddon = availableAddons.find((a) => moduleResolution.activeAddOnIds.has(a.id));
        if (activeAddon) {
          return {
            allowed: true,
            state: "included",
            reason: `Included via ${activeAddon.name} add-on`,
            source: "addon",
          };
        }
        return {
          allowed: false,
          state: "addon",
          reason: `Available as add-on: ${availableAddons[0].name}`,
          availableAddon: availableAddons[0].id,
        };
      }

      const minPlan = getMinPlanForCapability(capabilityKey);
      return {
        allowed: false,
        state: "locked",
        reason: minPlan ? `Requires ${minPlan.name} plan` : "Not available on current plan",
        upgradePlan: minPlan?.id as PlanId | undefined,
      };
    };

    return {
      billingStatus,
      hasUsableAccess,
      isDegraded,
      statusMessage,
      resolvedPlan: moduleResolution.resolvedPlan,
      daysUntilExpiry,
      resolution,
      moduleResolution,
      checkModuleAccess,
      checkCapabilityAccess,
      isLoading,
    };
  }, [resolution, moduleResolution, isLoading]);

  return <BillingAccessContext.Provider value={value}>{children}</BillingAccessContext.Provider>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useBillingAccess(): BillingAccessState {
  const ctx = useContext(BillingAccessContext);
  if (!ctx) {
    throw new Error("useBillingAccess must be used within BillingAccessProvider");
  }
  return ctx;
}

/**
 * Optional hook — returns null if outside provider (useful for components
 * that may or may not be within the billing context).
 */
export function useBillingAccessOptional(): BillingAccessState | null {
  return useContext(BillingAccessContext);
}
